// Unified event types for monitoring & history pages

// Shared user shape across all event types
export interface IEventUser {
  id: number;
  fullname: string;
  email: string;
  profilePicture?: string;
  phone?: string;
  entrepriseId?: number;
  hasVigilane?: boolean;
  subGroup?: {
    id: number;
    name: string;
  };
}

// Alert (emergency broadcast)
export interface IAlert {
  id: number;
  user: IEventUser;
  latitude: number;
  longitude: number;
  createdAt: string;
  updatedAt: string;
  isBroadcasting: boolean;
  status: AlertStatus;
}

export type AlertStatus = "NEW" | "ACCEPTED" | "CLOSED" | "CALLED";

// Journey
export interface IJourney {
  id: number;
  userId?: number;
  user: IEventUser;
  originLatitude: number;
  originLongitude: number;
  currentLatitude: number;
  currentLongitude: number;
  destinationLatitude: number;
  destinationLongitude: number;
  createdAt: string;
  updatedAt: string;
  lastHeartbeat: string;
  hasEnded: boolean;
}

// Forbidden zone definition (Place with zoneType=FORBIDDEN)
export interface IForbiddenZone {
  id: number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  radius: number;
  emoji?: string;
  zoneType: "FORBIDDEN";
  shapeType: "CIRCLE" | "POLYGON";
  polygon?: { lat: number; lng: number }[];
  isActive: boolean;
  entrepriseId?: number;
  subGroupId?: number;
  user?: IEventUser;
}

// Forbidden zone entry
export interface IForbiddenZoneHistory {
  id: number;
  placeId: number;
  userId: number;
  latitude: number;
  longitude: number;
  enteredAt: string;
  exitedAt: string | null;
  notifiedUserIds: number[];
  place: {
    id: number;
    name: string;
    emoji: string;
  };
  user: IEventUser;
}

// Event type discriminant
export const EVENT_TYPES = {
  alert: "alert",
  forbidden_zone: "forbidden_zone",
  journey: "journey",
} as const;

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

// Sort priority: lower = higher priority
export const EVENT_SORT_PRIORITY: Record<EventType, number> = {
  alert: 0,
  forbidden_zone: 1,
  journey: 2,
};

// Discriminated union for all event types
interface BaseUnifiedEvent {
  key: string; // `${eventType}-${id}`
  eventType: EventType;
  sortPriority: number;
  timestamp: string; // normalized date (ISO)
  isActive: boolean;
  userName: string;
  latitude: number;
  longitude: number;
}

export interface UnifiedAlertEvent extends BaseUnifiedEvent {
  eventType: typeof EVENT_TYPES.alert;
  data: IAlert;
}

export interface UnifiedForbiddenZoneEvent extends BaseUnifiedEvent {
  eventType: typeof EVENT_TYPES.forbidden_zone;
  data: IForbiddenZoneHistory;
}

export interface UnifiedJourneyEvent extends BaseUnifiedEvent {
  eventType: typeof EVENT_TYPES.journey;
  data: IJourney;
}

export type UnifiedEvent =
  | UnifiedAlertEvent
  | UnifiedForbiddenZoneEvent
  | UnifiedJourneyEvent;

// Filter types for history page
export interface UnifiedHistoryFilters {
  search?: string;
  eventType?: EventType;
  dateFrom?: string;
  dateTo?: string;
  hideActive?: boolean;
}
